import { messageChannelOf } from './message-channels';

// Mirrors services core/consent/consent.go. MESS-1345 removed legal_basis,
// ip_address and user_agent from the record — don't re-add them.

export const CONSENT_ACTIONS = ['opted_in', 'opted_out', 'voided'] as const;

export type ConsentAction = (typeof CONSENT_ACTIONS)[number];

export const DEFAULT_CONSENT_ACTION: ConsentAction = 'opted_in';

const CONSENT_ACTION_LABELS = new Map<ConsentAction, string>([
  ['opted_in', 'Opted in'],
  ['opted_out', 'Opted out'],
  ['voided', 'Voided'],
]);

// `voided` round-trips but isn't offered — nothing filters on it yet.
const PICKABLE_CONSENT_ACTIONS: readonly ConsentAction[] = ['opted_in', 'opted_out'];

export function consentActionOf(value: string): ConsentAction | null {
  return CONSENT_ACTIONS.find((action) => action === value) ?? null;
}

export const CONSENT_ACTION_OPTIONS: readonly { value: ConsentAction; label: string }[] = PICKABLE_CONSENT_ACTIONS.map(
  (value) => ({ value, label: CONSENT_ACTION_LABELS.get(value) ?? value }),
);

/** `CONSENT_ACTION_OPTIONS`, plus `selected` itself when it's `voided` or otherwise unpickable. */
export function consentActionOptions(selected: ConsentAction): readonly { value: ConsentAction; label: string }[] {
  return withSelected(CONSENT_ACTION_OPTIONS, selected, (value) => CONSENT_ACTION_LABELS.get(value) ?? value);
}

// Must match `consentIndexedFields` in services core/events/index.go.
export type ConsentQualifierOption = { value: string; label: string };

/**
 * Appends `selected` as a labeled option when the list doesn't carry it — a
 * value services can write but this row's picker doesn't offer (`voided`, a
 * qualifier value from before the vocabulary caught up). Without this the
 * select shows blank while the state is still the real, unchanged value.
 */
function withSelected<T extends string>(
  options: readonly { value: T; label: string }[],
  selected: T | null | undefined,
  labelFor: (value: T) => string,
): readonly { value: T; label: string }[] {
  if (selected == null || selected === '' || options.some((option) => option.value === selected)) {
    return options;
  }
  return [...options, { value: selected, label: labelFor(selected) }];
}

export const CONSENT_QUALIFIER_FIELDS = ['channel', 'collection_point', 'consent_state'] as const;

export type ConsentQualifierField = (typeof CONSENT_QUALIFIER_FIELDS)[number];

export function consentQualifierFieldOf(value: string): ConsentQualifierField | null {
  return CONSENT_QUALIFIER_FIELDS.find((field) => field === value) ?? null;
}

// The channels consent is actually managed on (`twilio` is stored as `sms`).
// Services can write others, but they aren't consent a person grants, so they
// aren't offered.
const CONSENT_CHANNEL_KEYS = ['email', 'sms', 'whatsapp', 'push'] as const;

type ConsentChannelKey = (typeof CONSENT_CHANNEL_KEYS)[number];

// Email is permanent and never appears in enabled_actions. An `sms` record can
// come from native SMS or Twilio, so either action offers it — `twilio` is in
// the list only when the workspace has Twilio credentials
// (services ui_api/environments/build_model.go).
const ALWAYS_AVAILABLE_CHANNELS: readonly ConsentChannelKey[] = ['email'];

const CHANNEL_ACTIONS = {
  email: [],
  sms: ['sms', 'twilio'],
  whatsapp: ['whatsapp'],
  push: ['push'],
} satisfies Record<ConsentChannelKey, readonly string[]>;

function channelOption(value: string): ConsentQualifierOption {
  return { value, label: messageChannelOf(value)?.label ?? value };
}

/** `selected` is always offered, so turning a channel off can't blank the select. */
export function consentChannelOptions(
  enabledActions: readonly string[],
  selected?: string | null,
): readonly ConsentQualifierOption[] {
  const offered = CONSENT_CHANNEL_KEYS.filter(
    (key) =>
      ALWAYS_AVAILABLE_CHANNELS.includes(key) ||
      CHANNEL_ACTIONS[key].some((action) => enabledActions.includes(action)) ||
      key === selected,
  ).map(channelOption);

  return withSelected(offered, selected, (value) => messageChannelOf(value)?.label ?? value);
}

const CONSENT_COLLECTION_POINT_OPTIONS = [
  { value: 'api', label: 'API' },
  { value: 'carrier_report', label: 'Carrier report' },
  { value: 'double_opt_in_confirmation', label: 'Double opt-in confirmation' },
  { value: 'import', label: 'Import' },
  { value: 'in_app_prompt', label: 'In-app prompt' },
  { value: 'inbound_reply', label: 'Inbound reply' },
  { value: 'list_unsubscribe_header', label: 'List-Unsubscribe header' },
  { value: 'one_click_unsubscribe', label: 'One-click unsubscribe' },
  { value: 'operator', label: 'Operator' },
  { value: 'subscription_center', label: 'Subscription center' },
  { value: 'unsubscribe_link', label: 'Unsubscribe link' },
] as const;

const CONSENT_STATE_OPTIONS = [
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
] as const;

const STATIC_QUALIFIER_OPTIONS = new Map<ConsentQualifierField, readonly ConsentQualifierOption[]>([
  ['collection_point', CONSENT_COLLECTION_POINT_OPTIONS],
  ['consent_state', CONSENT_STATE_OPTIONS],
]);

const CONSENT_QUALIFIER_FIELD_LABELS = new Map<ConsentQualifierField, string>([
  ['channel', 'Channel'],
  ['collection_point', 'Collection point'],
  ['consent_state', 'Consent state'],
]);

export function consentQualifierFieldLabel(field: ConsentQualifierField): string {
  return CONSENT_QUALIFIER_FIELD_LABELS.get(field) ?? field;
}

export function consentQualifierOptions(
  field: ConsentQualifierField,
  enabledActions: readonly string[],
  selected?: string | null,
): readonly ConsentQualifierOption[] {
  if (field === 'channel') {
    return consentChannelOptions(enabledActions, selected);
  }
  return withSelected(STATIC_QUALIFIER_OPTIONS.get(field) ?? [], selected, (value) => value);
}

export type ConsentConditionState = {
  action: ConsentAction;
  // One at most: two would AND across a person's records, not within one.
  field: ConsentQualifierField | null;
  value: string | null;
  inverse: boolean;
};
